import { css } from 'styled-components';
import * as lightThemeColors from '@prisjakt/design-tokens/light-theme-colors';
import * as measurements from '@prisjakt/design-tokens/measurements';
import { setRgbaAlpha } from '../src/color-utils';

const BASE_FONT_SIZE = parseInt(measurements.root, 10);
const PADDING_WRAPPER = 20;
const MARGIN_TOP_SUB_COMPONENT = 30;
const MARGIN_BOTTOM_SUB_COMPONENT = 30;
const GLOBAL_WIDTH = 1000;

// Transforms a pixel value to rem
// round to 5 digits to match Foundation rem-calc
export function remCalc(pixelSize: number) {
  const remSize = pixelSize / BASE_FONT_SIZE;
  return `${remSize.toFixed(5)}rem`;
}

// Transforms a rem value to pixel (without px unit)
export function pixelCalc(remSize: string) {
  return parseFloat(remSize) * BASE_FONT_SIZE;
}

export const colors = {
  backgroundDarkGray: setRgbaAlpha(lightThemeColors.base800, '0.05'),
  backgroundLightGray: 'rgba(0, 0, 0, 0.06)',
  borderDarkGray: 'rgba(0, 0, 0, 0.85);',
  borderLightGray: 'rgba(0, 0, 0, 0.09);',
  lightTheme: lightThemeColors,
};

export const campaignColors = {
  blackFriday: {
    id: 2,
    background: '#000',
    text: lightThemeColors.base0,
  },
  christmasSale: {
    id: 3,
    background: '#B70016',
    text: lightThemeColors.base0,
  },
  dailyDeals: {
    id: 4,
    background: '#9B051A',
    text: lightThemeColors.base0,
  },
  cyberMonday: {
    id: 6,
    background: '#5AA546',
    text: lightThemeColors.base0,
  },
  blackFridayWeekend: {
    id: 7,
    background: '#000',
    text: lightThemeColors.base0,
  },
  januarySale: {
    id: 12,
    background: '#4895B6',
    text: lightThemeColors.base0,
  },
};

export const brandColors = {
  twitter: '#00ACED',
  facebook: '#3B5998',
  pricespy: '#4895B6',
  expert: lightThemeColors.maybeBlue500,
};

export const ratingStarColors = {
  inactive: lightThemeColors.base300,
  active: lightThemeColors.yellove500,
};

const layoutMap = {
  measurements,
  fontSizes: {
    base: {
      mobile: {
        value: parseInt(measurements.fontSizeNormal, 10),
        rem: measurements.fontSizeNormal,
      },
      desktop: {
        value: parseInt(measurements.fontSizeNormal, 10),
        rem: measurements.fontSizeNormal,
      },
    },
    small: {
      mobile: {
        value: parseInt(measurements.fontSizeSmall, 10),
        rem: measurements.fontSizeSmall,
      },
      desktop: {
        value: parseInt(measurements.fontSizeSmall, 10),
        rem: measurements.fontSizeSmall,
      },
    },
    h1: {
      desktop: { value: 64, rem: remCalc(64) },
      mobile: { value: 36, rem: remCalc(36) },
    },
    h2: {
      desktop: { value: 24, rem: remCalc(24) },
      mobile: { value: 20, rem: remCalc(20) },
    },
    h3: {
      desktop: { value: 18, rem: remCalc(18) },
      mobile: { value: 18, rem: remCalc(18) },
    },
    h4: {
      desktop: { value: 17, rem: remCalc(17) },
      mobile: { value: 17, rem: remCalc(17) },
    },
    h5: {
      desktop: { value: 17, rem: remCalc(17) },
      mobile: { value: 17, rem: remCalc(17) },
    },
    h6: {
      desktop: { value: 16, rem: remCalc(16) },
      mobile: { value: 16, rem: remCalc(16) },
    },
  },

  margins: {
    wrapper: {
      mobile: { value: 12, rem: remCalc(12) },
      desktop: { value: 10, rem: remCalc(10) },
    },

    component: {
      mobile: { value: 20, rem: remCalc(20) },
      desktop: { value: 30, rem: remCalc(30) },
    },
  },

  headerHeight: {
    desktop: { value: 72, rem: remCalc(72) },
    mobile: { value: 48, rem: remCalc(48) },
    mobileWithSearch: { value: 101, rem: remCalc(101) },
  },

  fontFamily: 'Mulish, Helvetica Neue, Helvetica, Arial, Roboto, sans-serif',

  sidebarContentWidth: {
    desktop: { value: 304, rem: remCalc(304) },
  },

  sidebarMenuWidth: {
    desktop: { value: 56, rem: remCalc(56) },
  },

  pageContent: {
    gutter: { value: 10, rem: remCalc(10) },
    maxWidth: { value: 1300, rem: remCalc(1300) },
  },

  siteWidth: { value: GLOBAL_WIDTH, rem: remCalc(GLOBAL_WIDTH) },
  borderRadius: '5px',
  lineHeight: 1.5,

  stickyNavigationHeight: { value: 50, rem: remCalc(50) },

  dragAndDropHandleSize: {
    mobile: {
      value: 32,
      rem: remCalc(32),
    },
    desktop: { value: 16, rem: remCalc(16) },
  },

  pageContentMaxWidthVal: 1300,
  pageContentGutterVal: 10,

  // Ad layout
  takeOverTopMargin: '120px',
  takeOverWidth: '1600px',
  takeOverHeight: '900px',
  outsiderMinWidth: '200px',

  listSideMargin: '0.6rem',
  listSidePadding: '0.6rem',
  listSideHeight: '2.6rem',

  // Product card
  productCardMinWidthMobile: '146px',
  productCardMinWidth: '200px',
  productCardMaxWidth: '255px',
  productCardImageSize: '130px',
  productCardPaddingSmall: measurements.spacing025,
  productCardPaddingLarge: measurements.spacing100,

  // Foundation grid
  gridColumnGutter: {
    small: { value: 25, rem: remCalc(25) },
    large: { value: PADDING_WRAPPER, rem: remCalc(PADDING_WRAPPER) },
  },

  // Search
  searchBar: {
    height: {
      desktop: {
        value: 40,
        rem: remCalc(40),
      },
      mobile: {
        value: 35,
        rem: remCalc(35),
      },
    },
    borderWidth: 1,
  },

  animations: {
    buttonTransition: '150ms ease',
  },
};

const layoutMapDeprecated = {
  paddingWrapperVal: PADDING_WRAPPER,
  paddingWrapper: remCalc(PADDING_WRAPPER),
  marginTopSubComponentVal: MARGIN_TOP_SUB_COMPONENT,
  marginTopSubComponent: remCalc(MARGIN_TOP_SUB_COMPONENT),
  marginBottomSubComponentVal: MARGIN_BOTTOM_SUB_COMPONENT,
  marginBottomSubComponent: remCalc(MARGIN_BOTTOM_SUB_COMPONENT),
};

const fullLayoutMap = {
  ...layoutMapDeprecated,
  ...layoutMap,
};

export const layout =
  process.env.NODE_ENV === 'development'
    ? new Proxy(fullLayoutMap, {
        get: (obj, prop) => {
          if (prop in layoutMapDeprecated) {
            // eslint-disable-next-line no-console
            console.warn(
              `Deprecation: layout['${String(
                prop
              )}'] is deprecated in base, please refer to @prisjakt/ui-base to see the new value.`
            );
          }
          return obj[prop];
        },
      })
    : fullLayoutMap;

// Foundation breakpoints
export const breakpoints = {
  smallOnly: 'screen and (max-width: 39.9375em)',
  mediumUp: 'screen and (min-width: 40em)',
  mediumOnly: 'screen and (max-width: 63.9375em) and (min-width: 40em)',
  mediumDown: 'screen and (max-width: 63.9375em)',
  largeUp: 'screen and (min-width: 64em)',
  largeOnly: 'screen and (max-width: 74.9375em) and (min-width: 64em)',
  hideOutsiderAd: 'screen and (max-width: 1199px)',
  showOutsiderAd: 'screen and (min-width: 1200px)',
};

// Create media queries from a list of breakpoints
type MediaType = {
  [key in keyof typeof breakpoints]: typeof css;
};
// eslint-disable-next-line @typescript-eslint/no-shadow
type Parameters<T> = T extends (...args: infer T) => any ? T : never;
export const media: MediaType = Object.entries(breakpoints).reduce(
  (accumulator, [key, value]) => ({
    ...accumulator,
    [key]: ((...args: Parameters<typeof css>) => css`
      @media ${value} {
        ${css(...args)};
      }
    `) as typeof css,
  }),
  {} as MediaType
);
